import React, { useState, useMemo } from "react";
import { Table, DatePicker, Input } from "antd";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "use-debounce";
import { ShoppingBag, Search } from "lucide-react";
import dayjs from "dayjs";
import { ReportPage } from "../../../components";
import { GetInventoryReportPurchasedProducts } from "../../../Api";
import { DEBOUNCED_SEARCH_QUERY } from "../../../Constant";
import { exportToCSV, formatLKR } from "../../../lib";
import { useSectionAccess } from "../../../Services";
import { ReportAction } from "../../../Types";

const { RangePicker } = DatePicker;

export const PurchasedProductsReport: React.FC = () => {
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, "day"),
    dayjs(),
  ]);
  const [searchInput, setSearchInput] = useState("");
  const [search] = useDebounce(searchInput, DEBOUNCED_SEARCH_QUERY);

  const startDateStr = dateRange[0].format("YYYY-MM-DD");
  const endDateStr = dateRange[1].format("YYYY-MM-DD");

  const {
    data: responseData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["purchased-products-report", startDateStr, endDateStr, search],
    queryFn: () =>
      GetInventoryReportPurchasedProducts({
        start_date: startDateStr,
        end_date: endDateStr,
        search,
      }),
  });

  const reportData = useMemo(() => responseData?.data || [], [responseData]);
  const { isAllowed } = useSectionAccess("reports");

  const stats = useMemo(() => {
    const sumQty = reportData.reduce(
      (sum: number, item: any) => sum + (item.quantity || 0),
      0,
    );
    const sumTotal = reportData.reduce(
      (sum: number, item: any) => sum + (item.total || 0),
      0,
    );
    return [
      {
        title: "Distinct Line Items",
        value: `${reportData.length} Batches`,
        colorClass: "text-slate-655",
      },
      {
        title: "Total Volume Procured",
        value: `${sumQty} Pcs`,
        colorClass: "text-indigo-600",
      },
      {
        title: "Gross Procurement Spend",
        value: formatLKR(sumTotal),
        colorClass: "text-emerald-700 font-extrabold",
      },
    ];
  }, [reportData]);

  const columns = [
    {
      title: "Purchase Date",
      dataIndex: "purchase_date",
      key: "purchase_date",
      render: (v: string) => (
        <span className="text-xs text-gray-500">
          {dayjs(v).format("YYYY-MM-DD")}
        </span>
      ),
    },
    {
      title: "Product Title",
      dataIndex: "product_title",
      key: "product_title",
      render: (v: string) => (
        <span className="text-xs font-bold text-gray-800">{v}</span>
      ),
    },
    {
      title: "Invoice No",
      dataIndex: "invoice_no",
      key: "invoice_no",
      render: (v: string) => (
        <span className="text-xs font-bold text-gray-650">Inv: {v}</span>
      ),
    },
    {
      title: "Supplier",
      dataIndex: "supplier_name",
      key: "supplier_name",
      render: (v: string) => (
        <span className="text-xs text-gray-500 font-semibold">{v}</span>
      ),
    },
    {
      title: "Batch No",
      dataIndex: "batch_no",
      key: "batch_no",
      render: (v: string) => (
        <span className="text-xs font-semibold text-gray-770 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded">
          {v}
        </span>
      ),
    },
    {
      title: "Cost Price",
      dataIndex: "cost_price",
      key: "cost_price",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs font-semibold text-gray-655">
          LKR {v.toFixed(2)}
        </span>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      align: "center" as const,
      render: (v: number) => (
        <span className="text-xs font-bold text-gray-800">{v}</span>
      ),
    },
    {
      title: "Subtotal Spend",
      dataIndex: "total",
      key: "total",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs font-extrabold text-gray-955">
          LKR {v.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
  ];

  const filterControls = (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex flex-col gap-1.5 font-sans">
        <span className="text-[10px] font-bold text-gray-400 uppercase">
          Reporting Date Period
        </span>
        <RangePicker
          value={dateRange}
          onChange={(dates) =>
            dates && dates[0] && dates[1] && setDateRange([dates[0], dates[1]])
          }
          allowClear={false}
          className="h-9 rounded-lg border border-gray-200 shadow-sm text-xs font-semibold"
        />
      </div>

      <div className="flex flex-col gap-1.5 font-sans">
        <span className="text-[10px] font-bold text-gray-400 uppercase">
          Search Records
        </span>
        <Input
          placeholder="Search invoice no, product name..."
          prefix={<Search className="w-3.5 h-3.5 text-gray-450 mr-1.5" />}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          allowClear
          className="w-72 h-9 rounded-lg text-xs"
        />
      </div>
    </div>
  );

  return (
    <ReportPage
      title="Purchased Items Detail"
      description="Detailed itemized breakdown of products purchased from suppliers with unit cost and batch numbers."
      icon={<ShoppingBag className="w-6 h-6 text-emerald-600" />}
      backPath="/system/inventory-reports"
      stats={stats}
      onExportCSV={
        isAllowed(ReportAction.Export)
          ? () => {
              exportToCSV({
                data: reportData,
                fileName: "cash_flow_export",
                headers: ["date", "description", "income", "expense", "balance"],
              });
            }
          : undefined
      }
      isExportDisabled={reportData.length === 0 || !isAllowed(ReportAction.Export)}
      onRefresh={refetch}
      filterControls={filterControls}
    >
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden flex-1">
        <Table
          dataSource={reportData}
          columns={columns}
          loading={isLoading}
          rowKey={(record: any) => record.id || Math.random().toString()}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            className: "px-6 pb-2",
          }}
          scroll={{ x: 1000 }}
          className="custom-report-table"
        />
      </div>
    </ReportPage>
  );
};
